import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Alert, Row, Col, Table, Spinner, Modal, Form, InputGroup, ListGroup, ProgressBar } from 'react-bootstrap';
import { useOrder } from './OrderContext';
import { db, auth, default as app } from '../firebase';
import { collection, addDoc, doc, getDoc, updateDoc, setDoc, serverTimestamp, deleteDoc, getDocs, writeBatch } from 'firebase/firestore';
import { EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import { getFunctions, httpsCallable } from "firebase/functions";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// ★★★【修正】休日判定ロジックをタイムゾーン問題に影響されない方法に変更 ★★★
/**
 * 指定された日付が休日かどうかを判定する関数
 * @param {Date} date - チェックする日付
 * @param {object} weeklyHolidays - 定休日の設定
 * @param {Array} specialHolidays - 特別休日の設定
 * @returns {boolean} - 休日であればtrue
 */
const isHoliday = (date, weeklyHolidays, specialHolidays) => {
    // 曜日の定休日をチェック
    const dayOfWeek = date.getDay(); // ローカルタイムゾーンの曜日を取得 (0=Sunday)
    const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    if (weeklyHolidays[weekdays[dayOfWeek]]) {
        return true;
    }

    // Dateオブジェクトの時刻部分をリセットして、日付のみで比較するための準備
    const checkDateOnly = new Date(date);
    checkDateOnly.setHours(0, 0, 0, 0);

    // 特別休日をチェック
    for (const holiday of specialHolidays) {
        // FirestoreのTimestampをDateオブジェクトに変換し、時刻をリセット
        const startDate = holiday.startDate.toDate();
        startDate.setHours(0, 0, 0, 0);

        // 終了日も同様に時刻をリセット
        const endDate = holiday.endDate.toDate();
        endDate.setHours(0, 0, 0, 0);

        if (checkDateOnly >= startDate && checkDateOnly <= endDate) {
            return true;
        }
    }
    return false;
};

const getNextBusinessDay = (startDate, weeklyHolidays, specialHolidays) => {
  let nextDay = new Date(startDate);
  nextDay.setDate(nextDay.getDate() + 1);
  while (isHoliday(nextDay, weeklyHolidays, specialHolidays)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  return nextDay;
};

const calculateAddressLength = (address) => {
  const fullAddress = `${address.address1 || ''}${address.address2 || ''}${address.address3 || ''}`;
  let length = 0;
  for (let i = 0; i < fullAddress.length; i++) {
    length += fullAddress.charCodeAt(i).toString(16).length > 2 ? 2 : 1;
  }
  return length;
};
const AddressLengthIndicator = ({ address }) => {
  const MAX_ADDRESS_LENGTH = 150;
  const length = calculateAddressLength(address);
  const variant = length > MAX_ADDRESS_LENGTH ? 'danger' : 'success';
  return (
    <div className="mt-1">
      <ProgressBar now={length} max={MAX_ADDRESS_LENGTH} variant={variant} />
      <small className={`d-block text-end ${variant === 'danger' ? 'text-danger' : 'text-muted'}`}>
        文字数: {length} / {MAX_ADDRESS_LENGTH} (半角1文字、全角2文字で計算)
      </small>
    </div>
  );
};

function OrderConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId, orderData, shippingInfo1, shippingInfo2, showShipping2, resetOrder, saveNewAddress1, saveNewAddress2 } = useOrder();
  const addressCount = location.state?.addressCount || 0;
  const MAX_ADDRESSES = 30;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shippingDate, setShippingDate] = useState(null);
  const [error, setError] = useState('');
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationInfo, setRegistrationInfo] = useState({ email: '', password: '' });
  const [registrationError, setRegistrationError] = useState('');
  const [showAnonymousConfirmModal, setShowAnonymousConfirmModal] = useState(false);
  const [showAddressLimitModal, setShowAddressLimitModal] = useState(false);
  const [addressesToSave, setAddressesToSave] = useState([]);
  const [addressesToDelete, setAddressesToDelete] = useState([]);
  const [existingAddresses, setExistingAddresses] = useState([]);
  const [registrationSource, setRegistrationSource] = useState('shipping1');
  const [manualRegistrationInfo, setManualRegistrationInfo] = useState({
    personName: '', phoneNumber: '', zipCode: '', address1: '', address2: '', address3: '',
    companyName: '', companyPrefix: '', companySuffix: '', branchName: '', departmentName: '', isSoleProprietor: false,
  });
  const [modalFormErrors, setModalFormErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '発注内容確認 | My Order System';
    if ((!orderData || orderData.products.length === 0) && !isSubmitting) {
      navigate('/new-order');
      return;
    }
    const loadInitialData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('ログインしていません。');
        const passedUserInfo = location.state?.currentUserInfo;
        let userData;
        if (user.isAnonymous) {
          userData = passedUserInfo || { isAnonymous: true, personName: 'ゲスト様' };
        } else {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (!userDocSnap.exists()) throw new Error('ユーザーデータが見つかりません。');
          userData = userDocSnap.data();
        }
        setCurrentUserInfo(userData);
        const holidayDocRef = doc(db, 'holiday_settings', 'config');
        const systemDocRef = doc(db, 'system_settings', 'config');
        const [holidaySnap, systemSnap] = await Promise.all([getDoc(holidayDocRef), getDoc(systemDocRef)]);
        const holidaySettings = holidaySnap.exists() ? holidaySnap.data() : {};
        const fetchedSystemSettings = systemSnap.exists() ? systemSnap.data() : { orderChangeTimeLimit: 30 };
        const weeklyHolidays = holidaySettings.weeklyHolidays || {};
        const specialHolidays = holidaySettings.specialHolidays || [];
        const deliveryDeadline = fetchedSystemSettings.deliveryDeadline || '15:00';
        const now = new Date();
        let estimatedShippingDate = new Date(now);
        const [hours, minutes] = deliveryDeadline.split(':');
        const deadlineTime = new Date();
        deadlineTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        if (isHoliday(now, weeklyHolidays, specialHolidays) || now > deadlineTime) {
          estimatedShippingDate = getNextBusinessDay(now, weeklyHolidays, specialHolidays);
        } else if (isHoliday(now, weeklyHolidays, specialHolidays)) {
          estimatedShippingDate = getNextBusinessDay(now, weeklyHolidays, specialHolidays);
        }
        setShippingDate(estimatedShippingDate);
      } catch (err) {
        console.error("データ読み込みエラー:", err);
        setError(err.message || "データの読み込みに失敗しました。");
      } finally {
        setLoading(false);
      }
    };
    if (!isSubmitting) {
      loadInitialData();
    }
  }, [orderData, navigate, isSubmitting, location.state]);

  const placeOrder = async () => {
    const simplifiedProducts = orderData.products.map(p => ({
      partNumber: p.partNumber, quantity: p.quantity, remarks: p.remarks,
    }));

    // --- 注文を「更新」する場合のロジック ---
    if (orderId) {
      const orderDocRef = doc(db, 'orders', orderId);

      // ★★★【修正】ここから：注文番号の保持・再設定ロジックを反映 ★★★
      // 親の注文番号を取得（例: 20260126065）
      const baseOrderNumber = orderData.orderNumber; 

      const shippingDestinations = [];

      // 送付先1の処理：もし番号が消えていたら、親番号 + "00" を付け直す
      if (shippingInfo1.manualAddress.recipientName) {
          shippingDestinations.push({
              ...shippingInfo1.manualAddress,
              orderNumber: shippingInfo1.manualAddress.orderNumber || `${baseOrderNumber}00`
          });
      }

      // 送付先2の処理：もし番号が消えていたら、親番号 + "01" を付け直す
      if (showShipping2 && shippingInfo2.manualAddress.recipientName) {
          shippingDestinations.push({
              ...shippingInfo2.manualAddress,
              orderNumber: shippingInfo2.manualAddress.orderNumber || `${baseOrderNumber}01`
          });
      }
      // ★★★【修正】ここまで ★★★

      const dataToUpdate = {
        invoiceDetails: orderData.invoiceDetails || '',
        products: simplifiedProducts,
        shippingDestinations: shippingDestinations,
        updatedAt: serverTimestamp(),
        // ★★★【重要】「再印刷が必要」という目印を追加 ★★★
        needsReprint: true, 
      };
      
      await updateDoc(orderDocRef, dataToUpdate);
      alert('発注内容を更新しました。');
      
    // --- 新規に注文する場合のロジック ---
    } else {
      const functions = getFunctions(app, 'asia-northeast1');
      const createOrder = httpsCallable(functions, 'createOrder');

      const cleanShippingInfo1 = { ...shippingInfo1.manualAddress };
      delete cleanShippingInfo1.orderNumber;
      const cleanShippingInfo2 = { ...shippingInfo2.manualAddress };
      delete cleanShippingInfo2.orderNumber;

      const dataToSend = {
        orderData: {
          invoiceDetails: orderData.invoiceDetails || '',
          estimatedShippingDate: shippingDate ? `${shippingDate.getFullYear()}${(shippingDate.getMonth() + 1).toString().padStart(2, '0')}${shippingDate.getDate().toString().padStart(2, '0')}` : '',
          products: simplifiedProducts,
        },
        shippingInfo1: { manualAddress: cleanShippingInfo1 },
        shippingInfo2: { manualAddress: cleanShippingInfo2 },
        showShipping2: showShipping2,
      };

      await createOrder(dataToSend);
      alert('発注が完了しました。');
    }
  };

  const executePlaceOrder = async () => {
    if (currentUserInfo?.isAnonymous) {
      setShowAnonymousConfirmModal(true);
      return;
    }
    setSaving(true);
    setIsSubmitting(true);
    setError('');
    try {
      const user = auth.currentUser;
      const addressesToSavePromises = [];
      if (shippingInfo1.selectedId === 'manual' && saveNewAddress1 && !user.isAnonymous) {
        addressesToSavePromises.push(addDoc(collection(db, 'addresses', user.uid, 'shipping_addresses'), shippingInfo1.manualAddress));
      }
      if (showShipping2 && shippingInfo2.selectedId === 'manual' && saveNewAddress2 && !user.isAnonymous) {
        addressesToSavePromises.push(addDoc(collection(db, 'addresses', user.uid, 'shipping_addresses'), shippingInfo2.manualAddress));
      }
      await Promise.all(addressesToSavePromises);
      await placeOrder();
      navigate('/mainmenu');
      resetOrder();
    } catch (err) {
      setError('注文の確定に失敗しました。もう一度お試しください。');
      console.error("注文確定エラー:", err);
      setSaving(false);
      setIsSubmitting(false);
    }
  };
  
  const handleConfirmAndPlaceOrder = async () => {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) {
      await executePlaceOrder();
      return;
    }
    
    const newAddressesToSave = [];
    if (shippingInfo1.selectedId === 'manual' && saveNewAddress1) {
        newAddressesToSave.push(shippingInfo1.manualAddress);
    }
    if (showShipping2 && shippingInfo2.selectedId === 'manual' && saveNewAddress2) {
        newAddressesToSave.push(shippingInfo2.manualAddress);
    }

    if (newAddressesToSave.length > 0 && (addressCount + newAddressesToSave.length > MAX_ADDRESSES)) {
      const addresses = await getDocs(collection(db, 'addresses', user.uid, 'shipping_addresses'));
      setExistingAddresses(addresses.docs.map(d => ({ id: d.id, ...d.data() })));
      setAddressesToSave(newAddressesToSave);
      setAddressesToDelete([]);
      setShowAddressLimitModal(true);
      return;
    }

    await executePlaceOrder();
  };

  const handlePlaceOrderWithoutRegistering = async () => {
    setShowAnonymousConfirmModal(false);
    setSaving(true);
    setIsSubmitting(true);
    setError('');
    try {
      await placeOrder();
      navigate('/mainmenu');
      resetOrder();
    } catch (err) {
      setError('注文の確定に失敗しました。もう一度お試しください。');
      setSaving(false);
      setIsSubmitting(false);
    }
  };
  const handleRegisterAndOrder = () => {
    setShowAnonymousConfirmModal(false);
    setShowRegisterModal(true);
  };
  const handleLinkAccountAndPlaceOrder = async () => {
    setRegistrationError('');
    setModalFormErrors({});
    const newErrors = {};
    if (!registrationInfo.email) newErrors.email = true;
    if (!registrationInfo.password || registrationInfo.password.length < 6) newErrors.password = true;
    let sourceData;
    if (registrationSource === 'shipping1') {
      sourceData = { ...shippingInfo1.manualAddress, personName: shippingInfo1.manualAddress.recipientName, zipCode: shippingInfo1.manualAddress.postalCode };
    } else if (registrationSource === 'shipping2') {
      sourceData = { ...shippingInfo2.manualAddress, personName: shippingInfo2.manualAddress.recipientName, zipCode: shippingInfo2.manualAddress.postalCode };
    } else {
      sourceData = manualRegistrationInfo;
      if (!sourceData.personName) newErrors.personName = true;
      if (!sourceData.phoneNumber) newErrors.phoneNumber = true;
      if (!sourceData.zipCode) newErrors.zipCode = true;
      if (!sourceData.address1) newErrors.address1 = true;
      if (!sourceData.address2) newErrors.address2 = true;
      if (!sourceData.companyName && !sourceData.isSoleProprietor) newErrors.companyName = true;
      if (calculateAddressLength(sourceData) > 150) {
        setRegistrationError('住所の合計文字数が上限を超えています。');
        return;
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setRegistrationError('赤枠の必須項目を入力してください。');
      setModalFormErrors(newErrors);
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.isAnonymous) {
      setRegistrationError('匿名ユーザーのセッションが見つかりません。');
      return;
    }
    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(registrationInfo.email, registrationInfo.password);
      await linkWithCredential(currentUser, credential);
      const newCompanyId = uuidv4();
      const companyData = {
        companyId: newCompanyId,
        companyName: sourceData.companyName || '',
        companyPrefix: sourceData.companyPrefix || '',
        companySuffix: sourceData.companySuffix || '',
        branchName: sourceData.branchName || '',
        departmentName: sourceData.departmentName || '',
        isSoleProprietor: sourceData.isSoleProprietor || false,
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "companies", newCompanyId), companyData);
      const newUserData = {
        uid: currentUser.uid,
        personName: sourceData.personName,
        email: registrationInfo.email,
        phone: sourceData.phoneNumber,
        zipCode: sourceData.zipCode,
        address1: sourceData.address1,
        address2: sourceData.address2,
        address3: sourceData.address3,
        userAttribute: '一般ユーザー',
        userAttributeCode: '0001',
        companyId: newCompanyId,
        status: 'active',
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, 'users', currentUser.uid), newUserData);
      try {
        const functions = getFunctions(app, 'asia-northeast1');
        const notifyAdmins = httpsCallable(functions, 'notifyAdminsOfNewUser');
        await notifyAdmins({
          personName: newUserData.personName,
          email: newUserData.email,
          companyName: companyData.companyName,
          userAttribute: newUserData.userAttribute,
        });
      } catch (notificationError) {
        console.error("管理者への通知に失敗しました:", notificationError);
      }
      await placeOrder();
      navigate('/mainmenu');
      resetOrder();
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setRegistrationError('このメールアドレスは既に使用されています。');
      } else {
        setRegistrationError('登録に失敗しました。');
      }
      console.error("アカウント連携エラー:", err);
      setSaving(false);
    }
  };
  const handleManualRegInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setManualRegistrationInfo(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (modalFormErrors[name]) {
      setModalFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handleManualRegPhoneChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    setManualRegistrationInfo(prevInfo => {
      const currentParts = (prevInfo.phoneNumber || '').split('-');
      const partsForState = {
        part1: currentParts[0] || '',
        part2: currentParts[1] || '',
        part3: currentParts[2] || '',
      };
      partsForState[name] = sanitizedValue;
      const combinedPhone = [partsForState.part1, partsForState.part2, partsForState.part3].filter(Boolean).join('-');
      return { ...prevInfo, phoneNumber: combinedPhone };
    });
  };
  const handleAccountInfoChange = (e) => {
    const { name, value } = e.target;
    setRegistrationInfo(p => ({ ...p, [name]: value }));
    if (modalFormErrors[name]) {
      setModalFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleAddressToDeleteSelection = (addressId) => {
    setAddressesToDelete(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(addressId)) {
        newSelection.delete(addressId);
      } else {
        newSelection.add(addressId);
      }
      return Array.from(newSelection);
    });
  };

  const handleReplaceAndRegister = async () => {
    if (addressesToDelete.length !== addressesToSave.length) return;
    try {
      const userId = auth.currentUser.uid;
      const batch = writeBatch(db);
      addressesToDelete.forEach(addressId => {
        const docRef = doc(db, 'addresses', userId, 'shipping_addresses', addressId);
        batch.delete(docRef);
      });
      await batch.commit();
      setShowAddressLimitModal(false);
      alert(`${addressesToDelete.length}件の既存の住所を削除しました。\n注文処理を続行します。`);
      await executePlaceOrder();
    } catch (err) {
      alert('処理に失敗しました。');
      console.error(err);
    }
  };

  const handleManualRegPostalCodeSearch = useCallback(async () => {
    if (manualRegistrationInfo.zipCode && manualRegistrationInfo.zipCode.length === 7) {
      try {
        const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: manualRegistrationInfo.zipCode } });
        if (res.data.results) {
          const result = res.data.results[0];
          setManualRegistrationInfo(p => ({ ...p, address1: result.address1, address2: result.address2 + result.address3, address3: '' }));
        } else {
          alert('該当する住所が見つかりませんでした。');
        }
      } catch (err) {
        alert('住所の自動入力に失敗しました。');
      }
    } else {
      alert('7桁の郵便番号を入力してください。');
    }
  }, [manualRegistrationInfo.zipCode]);
  const getPhoneParts = (phoneString) => {
    const parts = (phoneString || '').split('-');
    return {
      part1: parts[0] || '',
      part2: parts[1] || '',
      part3: parts[2] || '',
    };
  };
  let manualRegPhoneParts;
  if (registrationSource === 'manual') {
    manualRegPhoneParts = getPhoneParts(manualRegistrationInfo.phoneNumber);
  } else if (registrationSource === 'shipping1') {
    manualRegPhoneParts = getPhoneParts(shippingInfo1.manualAddress.phoneNumber);
  } else {
    manualRegPhoneParts = getPhoneParts(shippingInfo2.manualAddress.phoneNumber);
  }

  if (!orderData) return null;
  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">
            {orderId ? '発注内容変更の確認' : '発注内容の確認'}
          </Card.Header>
          <Card.Body className="p-4">
            <Alert variant="info">以下の内容で発注します。よろしいですか？</Alert>
            {error && <Alert variant="danger">{error}</Alert>}
            <section className="mb-4">
              <h3>基本情報</h3>
              <Table bordered>
                <tbody>
                  <tr>
                    <td className="bg-light w-25">送り状に記載する事項</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{orderData.invoiceDetails}</td>
                  </tr>
                  <tr>
                    <td className="bg-light">出荷予定日</td>
                    <td>{loading ? <Spinner size="sm" /> : shippingDate ? <strong>{shippingDate.toLocaleDateString('ja-JP')}</strong> : '計算中...'}</td>
                  </tr>
                </tbody>
              </Table>
            </section>
            <Row>
              <Col lg={6} className="mb-4">
                <section>
                  <h3>送付先1</h3>
                  <Table bordered>
                    <tbody>
                      <tr><td className="bg-light w-25">会社名</td><td>{shippingInfo1.manualAddress.shippingCompanyName}</td></tr>
                      <tr><td className="bg-light">支店・部署名</td><td>{`${shippingInfo1.manualAddress.branchName || ''} ${shippingInfo1.manualAddress.departmentName || ''}`}</td></tr>
                      <tr><td className="bg-light w-25">氏名</td><td>{shippingInfo1.manualAddress.recipientName}</td></tr>
                      <tr><td className="bg-light">電話番号</td><td>{shippingInfo1.manualAddress.phoneNumber}</td></tr>
                      <tr><td className="bg-light">住所</td><td>〒{shippingInfo1.manualAddress.postalCode} {shippingInfo1.manualAddress.address1}{shippingInfo1.manualAddress.address2}{shippingInfo1.manualAddress.address3}</td></tr>
                    </tbody>
                  </Table>
                </section>
              </Col>
              {showShipping2 && shippingInfo2.manualAddress.recipientName && (
                <Col lg={6} className="mb-4">
                  <section>
                    <h3>送付先2</h3>
                    <Table bordered>
                      <tbody>
                        <tr><td className="bg-light w-25">会社名</td><td>{shippingInfo2.manualAddress.shippingCompanyName}</td></tr>
                        <tr><td className="bg-light">支店・部署名</td><td>{`${shippingInfo2.manualAddress.branchName || ''} ${shippingInfo2.manualAddress.departmentName || ''}`}</td></tr>
                        <tr><td className="bg-light w-25">氏名</td><td>{shippingInfo2.manualAddress.recipientName}</td></tr>
                        <tr><td className="bg-light">電話番号</td><td>{shippingInfo2.manualAddress.phoneNumber}</td></tr>
                        <tr><td className="bg-light">住所</td><td>〒{shippingInfo2.manualAddress.postalCode} {shippingInfo2.manualAddress.address1}{shippingInfo2.manualAddress.address2}{shippingInfo2.manualAddress.address3}</td></tr>
                      </tbody>
                    </Table>
                  </section>
                </Col>
              )}
            </Row>
            <section>
              <h3>発注商品リスト</h3>
              <Table striped bordered hover responsive>
                <thead><tr><th>品番</th><th>数量</th><th>備考</th></tr></thead>
                <tbody>{orderData.products.map((p, i) => (<tr key={`${p.partNumber}-${i}`}><td>{p.partNumber}</td><td>{p.quantity}</td><td>{p.remarks}</td></tr>))}</tbody>
              </Table>
            </section>
            <Row className="g-2 mt-4">
              <Col md={6}><div className="d-grid"><Button variant="secondary" size="lg" onClick={() => navigate(-1)}>修正する</Button></div></Col>
              <Col md={6}><div className="d-grid"><Button variant="primary" size="lg" onClick={handleConfirmAndPlaceOrder} disabled={loading || saving}>{saving ? <Spinner as="span" size="sm" /> : '注文を確定する'}</Button></div></Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
      <Modal show={showAnonymousConfirmModal} onHide={() => setShowAnonymousConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>ご注文方法の選択</Modal.Title></Modal.Header>
        <Modal.Body><p>ユーザー登録を行うと、次回から送付先の入力が簡単になります。</p><p>ご登録されますか？</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAnonymousConfirmModal(false)}>キャンセル</Button>
          <Button variant="outline-primary" onClick={handlePlaceOrderWithoutRegistering} disabled={saving}>{saving ? <Spinner size="sm" /> : "登録せず注文確定"}</Button>
          <Button variant="primary" onClick={handleRegisterAndOrder}>登録して注文確定</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>ユーザー登録</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>アカウント情報を入力してください。</p>
          {registrationError && <Alert variant="danger">{registrationError}</Alert>}
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label>登録に使用する情報</Form.Label>
              <div>
                <Form.Check inline type="radio" label="送付先1の情報" name="regSource" id="regSource1" value="shipping1" checked={registrationSource === 'shipping1'} onChange={(e) => setRegistrationSource(e.target.value)} />
                {showShipping2 && shippingInfo2.manualAddress.recipientName && <Form.Check inline type="radio" label="送付先2の情報" name="regSource" id="regSource2" value="shipping2" checked={registrationSource === 'shipping2'} onChange={(e) => setRegistrationSource(e.target.value)} />}
                <Form.Check inline type="radio" label="新しい情報を入力" name="regSource" id="regSourceManual" value="manual" checked={registrationSource === 'manual'} onChange={(e) => setRegistrationSource(e.target.value)} />
              </div>
            </Form.Group>
            <div className="p-3 mb-3 border rounded">
              <h5>ユーザー・会社情報</h5>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>氏名</Form.Label><Form.Control type="text" name="personName" value={registrationSource === 'manual' ? manualRegistrationInfo.personName : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress.recipientName : shippingInfo2.manualAddress.recipientName)} onChange={handleManualRegInfoChange} disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.personName} required /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>電話番号</Form.Label><Row className="g-2 align-items-center"><Col><Form.Control type="tel" name="part1" value={manualRegPhoneParts.part1} onChange={handleManualRegPhoneChange} maxLength="5" disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.phoneNumber} required /></Col><Col xs="auto">-</Col><Col><Form.Control type="tel" name="part2" value={manualRegPhoneParts.part2} onChange={handleManualRegPhoneChange} maxLength="4" disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.phoneNumber} required /></Col><Col xs="auto">-</Col><Col><Form.Control type="tel" name="part3" value={manualRegPhoneParts.part3} onChange={handleManualRegPhoneChange} maxLength="4" disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.phoneNumber} required /></Col></Row></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>郵便番号</Form.Label><InputGroup><Form.Control type="text" name="zipCode" value={registrationSource === 'manual' ? manualRegistrationInfo.zipCode : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress.postalCode : shippingInfo2.manualAddress.postalCode)} onChange={handleManualRegInfoChange} maxLength="7" disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.zipCode} required /><Button variant="secondary" onClick={handleManualRegPostalCodeSearch} disabled={registrationSource !== 'manual'}>住所検索</Button></InputGroup></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>住所1</Form.Label><Form.Control type="text" name="address1" value={registrationSource === 'manual' ? manualRegistrationInfo.address1 : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress.address1 : shippingInfo2.manualAddress.address1)} onChange={handleManualRegInfoChange} disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.address1} required /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>住所2</Form.Label><Form.Control type="text" name="address2" value={registrationSource === 'manual' ? manualRegistrationInfo.address2 : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress.address2 : shippingInfo2.manualAddress.address2)} onChange={handleManualRegInfoChange} disabled={registrationSource !== 'manual'} isInvalid={!!modalFormErrors.address2} required /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>住所3</Form.Label><Form.Control type="text" name="address3" value={registrationSource === 'manual' ? manualRegistrationInfo.address3 : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress.address3 : shippingInfo2.manualAddress.address3)} onChange={handleManualRegInfoChange} disabled={registrationSource !== 'manual'} /></Form.Group></Col>
                <Col md={12}><AddressLengthIndicator address={registrationSource === 'manual' ? manualRegistrationInfo : (registrationSource === 'shipping1' ? shippingInfo1.manualAddress : shippingInfo2.manualAddress)} /></Col>
              </Row>
              <hr />
              <Row>
                <Col md={12}><Form.Group className="mb-3"><Form.Check type="checkbox" name="isSoleProprietor" label="個人事業主" checked={manualRegistrationInfo.isSoleProprietor} onChange={handleManualRegInfoChange} /></Form.Group></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>会社名</Form.Label><Form.Control type="text" name="companyName" value={manualRegistrationInfo.companyName} onChange={handleManualRegInfoChange} isInvalid={!!modalFormErrors.companyName} required /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>法人格 (前)</Form.Label><Form.Select name="companyPrefix" value={manualRegistrationInfo.companyPrefix} onChange={handleManualRegInfoChange} disabled={manualRegistrationInfo.isSoleProprietor}><option value="">なし</option><option value="株式会社">株式会社</option><option value="有限会社">有限会社</option></Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>法人格 (後)</Form.Label><Form.Select name="companySuffix" value={manualRegistrationInfo.companySuffix} onChange={handleManualRegInfoChange} disabled={manualRegistrationInfo.isSoleProprietor}><option value="">なし</option><option value="株式会社">株式会社</option><option value="有限会社">有限会社</option></Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>支店名</Form.Label><Form.Control type="text" name="branchName" value={manualRegistrationInfo.branchName} onChange={handleManualRegInfoChange} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>部署名</Form.Label><Form.Control type="text" name="departmentName" value={manualRegistrationInfo.departmentName} onChange={handleManualRegInfoChange} /></Form.Group></Col>
              </Row>
            </div>
            <hr />
            <h5>アカウント情報</h5>
            <Form.Group className="mb-3"><Form.Label>メールアドレス</Form.Label><Form.Control type="email" name="email" value={registrationInfo.email} onChange={handleAccountInfoChange} isInvalid={!!modalFormErrors.email} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>パスワード (6文字以上)</Form.Label><Form.Control type="password" name="password" value={registrationInfo.password} onChange={handleAccountInfoChange} isInvalid={!!modalFormErrors.password} required /></Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRegisterModal(false)}>キャンセル</Button>
          <Button variant="primary" onClick={handleLinkAccountAndPlaceOrder} disabled={saving}>{saving ? <Spinner as="span" size="sm" /> : "登録して注文を確定する"}</Button>
        </Modal.Footer>
      </Modal>
      {/* 【変更点】上限超過モーダルのUIとロジックを修正 */}
      <Modal show={showAddressLimitModal} onHide={() => setShowAddressLimitModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">送付先リストの登録上限に達しました</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            新しい送付先を {addressesToSave.length} 件登録するには、既存のリストから同じ数の送付先を削除する必要があります。
          </p>
          <p>
            削除したい送付先を <strong>{addressesToSave.length} 件</strong> 選択してください。
            現在 {addressesToDelete.length} 件選択中
          </p>
          <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {existingAddresses.map(addr => (
              <ListGroup.Item key={addr.id}>
                <Form.Check 
                  type="checkbox"
                  id={`delete-addr-${addr.id}`}
                  checked={addressesToDelete.includes(addr.id)}
                  onChange={() => handleAddressToDeleteSelection(addr.id)}
                  label={
                    <div>
                      <strong>{addr.recipientName}</strong><br />
                      <small>〒{addr.postalCode} {addr.address1}{addr.address2}{addr.address3}</small>
                    </div>
                  }
                />
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddressLimitModal(false)}>キャンセル</Button>
          <Button 
            variant="danger" 
            onClick={handleReplaceAndRegister}
            disabled={addressesToDelete.length !== addressesToSave.length}
          >
            選択した {addressesToDelete.length} 件を削除して登録
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}
export default OrderConfirmation;